/**
 * `lazyPrimitive` — wrap a code-split primitive component as a `PrimitiveComponent`.
 *
 * Some primitives pull heavy, environment-specific deps (the `@react-pdf/renderer` report renderer, the
 * pptx/deck builders, the install-base parse worker). Loading them lazily keeps those deps out of the
 * registration hub's static import graph — so the main bundle stays lean and the hub (and its jest test)
 * never eagerly parse jest-hostile modules. `StepBodyRenderer` renders every primitive inside a single
 * `Suspense` boundary, so a lazy primitive needs no local boundary of its own.
 */
import { lazy, type ComponentType } from 'react';
import type { PrimitiveComponent, PrimitiveProps } from './registry';

/** Turn a dynamic import of a `PrimitiveProps` component into a registered `PrimitiveComponent`. */
export function lazyPrimitive(
  load: () => Promise<{ default: ComponentType<PrimitiveProps> }>,
): PrimitiveComponent {
  const Lazy = lazy(load);
  // Named (not anonymous) so it carries a display name for React devtools / the display-name lint.
  return function LazyPrimitive(props: PrimitiveProps) {
    return <Lazy {...props} />;
  };
}
